<script lang="ts">
	// UC-14. The restaurant's shops, one at a time: pick one by name, change
	// its details, its optional phone, its delivery areas, its tables
	// (UC-39, Tables.svelte) and its drivers (UC-65, Drivers.svelte). Receipt
	// words are shared by every shop and live in Settings → Printing
	// (Printing.svelte); a save sends a shop's old receipt words back
	// unchanged, so the stored rows stay as they were.
	//
	// Closing a shop is undone, not confirmed: it leaves at once and the
	// server hears of it when the Undo runs out (owner/notices.svelte.ts).
	// A fee is read as it is written, so 25 is 25.00 (owner/price.svelte.ts),
	// and left empty it is nothing.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Tables from './Tables.svelte';
	import Drivers from './Drivers.svelte';
	import { api } from '../api/client';
	import { message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import type { Account } from '../owner/session.svelte';
	import { loadShops, ownerShops, type Shop } from '../owner/data.svelte';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import { PriceInput } from '../owner/price.svelte';
	import type { components } from '../api/types';

	type Area = components['schemas']['AreaBody'];

	interface Props {
		language: Language;
		account: Account;
		/** Bumped by the title row's "New shop". */
		actionAsked: number;
		/** A shop to open, as the catalogue's "Address in Shops" asks. */
		shopAsked?: string;
		shopId?: string;
		view?: 'all' | 'list' | 'settings' | 'delivery';
		onOpenShop?: (id: string) => void;
	}

	let {
		language,
		account,
		actionAsked,
		shopAsked = '',
		shopId = '',
		view = 'all',
		onOpenShop,
	}: Props = $props();

	const minorDigits = $derived(account.settings.minorDigits);
	const currency = $derived(account.settings.currencyCode);
	const shops = $derived(ownerShops());

	interface Form {
		name: string;
		address: string;
		phone: string;
		dayStart: string;
		fee: PriceInput;
		header: string;
		footer: string;
	}

	let chosen = $state('');
	let isNew = $state(false);
	let form = $state<Form>(blank());
	let nameError = $state('');
	let phoneError = $state('');
	let failure = $state('');
	let lastShop = $state(false);
	let saving = $state(false);

	const selected = $derived(
		isNew
			? undefined
			: shopId
				? shops.value.find((b) => b.id === shopId)
				: (shops.value.find((b) => b.id === chosen) ?? shops.value[0]),
	);

	function feeInput(minor: number | null): PriceInput {
		const fee = new PriceInput(() => minorDigits, { optional: true });
		fee.load(minor);
		return fee;
	}

	function blank(): Form {
		return {
			name: '',
			address: '',
			phone: '',
			dayStart: '4',
			fee: feeInput(null),
			header: '',
			footer: '',
		};
	}

	function formOf(shop: Shop): Form {
		return {
			name: shop.name,
			address: shop.address,
			phone: shop.phone,
			dayStart: String(shop.dayStartHour),
			fee: feeInput(shop.defaultDeliveryFeeMinor),
			header: shop.receiptHeader,
			footer: shop.receiptFooter,
		};
	}

	// The form follows the shop on screen; a new one starts blank.
	let formFor = $state('');
	$effect(() => {
		const key = isNew ? 'new' : (selected?.id ?? '');
		if (key === formFor) return;
		formFor = key;
		form = selected ? formOf(selected) : blank();
		nameError = '';
		phoneError = '';
		failure = '';
		lastShop = false;
		areaEdit = null;
		if (selected) void loadAreas(selected.id);
	});

	// The title row's "New shop".
	$effect(() => {
		if (actionAsked > 0) isNew = true;
	});

	// The catalogue's "Address in Shops".
	$effect(() => {
		if (shopAsked) {
			isNew = false;
			chosen = shopAsked;
		}
	});

	async function save() {
		if (saving || !form.name.trim() || form.dayStart === '') return;
		form.fee.tidy();
		if (!form.fee.valid) return;
		saving = true;
		nameError = '';
		failure = '';
		const body = {
			name: form.name.trim(),
			address: form.address.trim(),
			phone: form.phone.trim(),
			dayStartHour: Number(form.dayStart),
			defaultDeliveryFeeMinor: form.fee.minor ?? 0,
			receiptHeader: form.header,
			receiptFooter: form.footer,
		};
		try {
			const result = isNew
				? await api.POST('/api/owner/shops', { body })
				: await api.PATCH('/api/owner/shops/{shopId}', {
						params: { path: { shopId: selected?.id ?? '' } },
						body,
					});
			if (result.error !== undefined || !result.data) {
				if (result.error?.code === 'SHOP_NAME_IN_USE') {
					nameError = message(language, 'shopsDupe');
				} else if (String(result.error?.details?.field ?? '') === 'phone') {
					phoneError = message(language, 'shopsPhoneInvalid');
				} else {
					failure = result.error?.message ?? message(language, 'loadFailed');
				}
				return;
			}
			const opened = isNew;
			await loadShops(message(language, 'loadFailed'));
			chosen = result.data.id;
			isNew = false;
			flash(message(language, opened ? 'shopsOpened' : 'saved'));
			if (opened && view === 'list') onOpenShop?.(result.data.id);
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			saving = false;
		}
	}

	function closeShop() {
		const shop = selected;
		if (!shop) return;
		if (shops.value.length <= 1) {
			lastShop = true;
			return;
		}
		const at = shops.value.indexOf(shop);
		shops.value = shops.value.filter((b) => b.id !== shop.id);
		chosen = shops.value[0]?.id ?? '';
		removeWithUndo(
			`${message(language, 'shopsClosed')} ${shop.name}`,
			async () => {
				try {
					await api.POST('/api/owner/shops/{shopId}/deactivate', {
						params: { path: { shopId: shop.id } },
					});
				} finally {
					await loadShops(message(language, 'loadFailed'));
				}
			},
			() => {
				shops.value.splice(at, 0, shop);
				chosen = shop.id;
			},
		);
	}

	// ---------- delivery areas ----------

	let areas = $state<Area[]>([]);
	let areasLoaded = $state(false);
	let areaEdit = $state<{ id: string; name: string; fee: PriceInput } | null>(null);
	let areaBusy = $state(false);

	async function loadAreas(shopId: string) {
		areasLoaded = false;
		try {
			const { data } = await api.GET('/api/owner/shops/{shopId}/areas', {
				params: { path: { shopId } },
			});
			areas = data?.areas ?? [];
		} catch {
			areas = [];
		} finally {
			areasLoaded = true;
		}
	}

	async function saveArea() {
		const shop = selected;
		if (!shop || !areaEdit || !areaEdit.name.trim() || areaBusy) return;
		failure = '';
		areaEdit.fee.tidy();
		if (!areaEdit.fee.valid) return;
		areaBusy = true;
		const body = { name: areaEdit.name.trim(), feeMinor: areaEdit.fee.minor ?? 0 };
		try {
			const { error } =
				areaEdit.id === 'new'
					? await api.POST('/api/owner/shops/{shopId}/areas', {
							params: { path: { shopId: shop.id } },
							body,
						})
					: await api.PATCH('/api/owner/areas/{areaId}', {
							params: { path: { areaId: areaEdit.id } },
							body,
						});
			if (error !== undefined) {
				failure = error.message ?? message(language, 'loadFailed');
				return;
			}
			areaEdit = null;
			await loadAreas(shop.id);
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			areaBusy = false;
		}
	}

	async function removeArea(area: Area) {
		const shop = selected;
		if (!shop || areaBusy) return;
		areaBusy = true;
		try {
			const { error } = await api.POST('/api/owner/areas/{areaId}/remove', {
				params: { path: { areaId: area.id } },
			});
			if (error !== undefined) failure = error.message ?? message(language, 'loadFailed');
			await loadAreas(shop.id);
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			areaBusy = false;
		}
	}
</script>

<div class="shops" data-testid="panel-shops">
	{#if !shops.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	{:else}
		{#if lastShop}
			<Band tone="flag" testId="shops-last">{message(language, 'shopsLast')}</Band>
		{/if}
		{#if failure || shops.failure}
			<Band tone="flag" testId="shops-error">{failure || shops.failure}</Band>
		{/if}

		{#if view === 'list' && !isNew}
			<div class="grid">
				{#each shops.value as shop (shop.id)}
					<Card title={shop.name} pad>
						<p class="soft">{shop.address}</p>
						<Button
							testId="shop-open-{shop.id}"
							label={message(language, 'ownerOpenShop')}
							tone="secondary"
							onPress={() => onOpenShop?.(shop.id)}
						/>
					</Card>
				{/each}
			</div>
		{:else}
			{#if view === 'all'}
				<div class="tabs">
					{#each shops.value as shop (shop.id)}
						<Button
							testId="shop-tab-{shop.id}"
							label={shop.name}
							tone={!isNew && shop.id === selected?.id ? 'ink' : 'secondary'}
							size="sm"
							onPress={() => {
								isNew = false;
								chosen = shop.id;
							}}
						/>
					{/each}
				</div>
			{/if}
			<div class="grid">
				<Card
					title={isNew ? message(language, 'shopsAdd') : message(language, 'shopsDetails')}
					testId="shop-form"
				>
					<div class="form">
						{#if isNew || view === 'all' || view === 'settings'}
							<Field
								testId="shop-name"
								label={message(language, 'shopsName')}
								value={form.name}
								error={nameError || undefined}
								maxLength={120}
								onInput={(v) => {
									form.name = v;
									nameError = '';
								}}
							/>
							<Field
								testId="shop-address"
								label={message(language, 'shopsAddress')}
								value={form.address}
								maxLength={240}
								onInput={(v) => (form.address = v)}
							/>
							<Field
								testId="shop-phone"
								label={message(language, 'shopsPhone')}
								value={form.phone}
								note={message(language, 'shopsPhoneNote')}
								inputMode="tel"
								maxLength={32}
								error={phoneError || undefined}
								onInput={(v) => {
									form.phone = v;
									phoneError = '';
								}}
							/>
						{/if}
						<div class="pair">
							{#if isNew || view === 'all' || view === 'settings'}
								<Field
									testId="shop-day-start"
									label={message(language, 'shopsDayStart')}
									value={form.dayStart}
									note="{message(language, 'shopsDayStartBefore')} {form.dayStart || 0} {message(
										language,
										'shopsDayStartAfter',
									)}"
									inputMode="numeric"
									mono
									onInput={(v) => {
										const hour = v.replace(/\D/g, '').slice(0, 2);
										if (hour === '' || Number(hour) <= 23) form.dayStart = hour;
									}}
								/>
							{/if}
							{#if isNew || view === 'all' || view === 'delivery'}
								<Field
									testId="shop-fee"
									label={message(language, 'shopsFee')}
									value={form.fee.text}
									placeholder={formatMinor(0, minorDigits)}
									note={currency}
									error={form.fee.error(language)}
									inputMode="decimal"
									mono
									onInput={form.fee.set}
									onBlur={form.fee.tidy}
								/>
							{/if}
						</div>
						<div class="actions">
							<Button
								testId="shop-save"
								label={isNew ? message(language, 'shopsOpen') : message(language, 'save')}
								size="md"
								state={saving ? 'pending' : 'idle'}
								pendingLabel={message(language, 'saving')}
								disabled={!form.name.trim() || form.dayStart === ''}
								onPress={() => void save()}
							/>
							{#if isNew}
								<Button
									testId="shop-cancel"
									label={message(language, 'cancel')}
									tone="text-quiet"
									size="sm"
									onPress={() => (isNew = false)}
								/>
							{:else if view === 'all' || view === 'settings'}
								<Button
									testId="shop-close"
									label={message(language, 'shopsClose')}
									tone="text-quiet"
									size="sm"
									onPress={closeShop}
								/>
								<span class="soft">{message(language, 'shopsCloseNote')}</span>
							{/if}
						</div>
					</div>
				</Card>

				<div class="side">
					{#if selected && (view === 'all' || view === 'delivery')}
						<Card title={message(language, 'areasTitle')} testId="areas-{selected.id}">
							<p class="note">
								{message(language, 'areasNoArea')}
								<bdi class="n" dir="ltr"
									>{formatMinor(selected.defaultDeliveryFeeMinor, minorDigits)}</bdi
								>
							</p>
							{#if areasLoaded && areas.length === 0 && areaEdit?.id !== 'new'}
								<p class="empty">{message(language, 'areasEmpty')}</p>
							{/if}
							{#each [...areas, ...(areaEdit?.id === 'new' ? [{ id: 'new', name: '', feeMinor: 0, shopId: '' }] : [])] as area (area.id)}
								<div class="area" data-testid="area-{area.id}">
									{#if areaEdit && areaEdit.id === area.id}
										<div class="area-name">
											<Field
												testId="area-name"
												ariaLabel={message(language, 'areasName')}
												value={areaEdit.name}
												maxLength={120}
												onInput={(v) => areaEdit && (areaEdit.name = v)}
											/>
										</div>
										<div class="area-fee">
											<Field
												testId="area-fee"
												ariaLabel={message(language, 'areasFee')}
												value={areaEdit.fee.text}
												placeholder={formatMinor(0, minorDigits)}
												error={areaEdit.fee.error(language)}
												inputMode="decimal"
												mono
												onInput={areaEdit.fee.set}
												onBlur={areaEdit.fee.tidy}
											/>
										</div>
										<Button
											testId="area-save"
											label={message(language, 'save')}
											size="sm"
											disabled={!areaEdit.name.trim() || areaBusy}
											onPress={() => void saveArea()}
										/>
										<Button
											testId="area-cancel"
											label={message(language, 'cancel')}
											tone="text-quiet"
											size="sm"
											onPress={() => (areaEdit = null)}
										/>
									{:else}
										<span class="area-label">{area.name}</span>
										<span class="n strong" dir="ltr">{formatMinor(area.feeMinor, minorDigits)}</span
										>
										<Button
											testId="area-edit-{area.id}"
											label={message(language, 'edit')}
											tone="text"
											size="sm"
											onPress={() =>
												(areaEdit = { id: area.id, name: area.name, fee: feeInput(area.feeMinor) })}
										/>
										<Button
											testId="area-remove-{area.id}"
											label={message(language, 'remove')}
											tone="text-quiet"
											size="sm"
											disabled={areaBusy}
											onPress={() => void removeArea(area)}
										/>
									{/if}
								</div>
							{/each}
							<div class="add">
								<Button
									testId="area-add"
									icon="plus"
									label={message(language, 'areasAdd')}
									tone="secondary"
									size="sm"
									onPress={() => (areaEdit = { id: 'new', name: '', fee: feeInput(null) })}
								/>
							</div>
						</Card>
						{#if view === 'all'}<Tables {language} shopId={selected.id} />
							<Drivers {language} shopId={selected.id} />{/if}
					{/if}
				</div>
			</div>
		{/if}
	{/if}
</div>

<style>
	.shops {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.tabs {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
		gap: var(--grid-gap);
		align-items: start;
	}

	.form {
		padding: var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.pair {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
		gap: var(--space-7);
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-4);
		align-items: center;
	}

	.soft {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.side {
		display: flex;
		flex-direction: column;
		gap: var(--grid-gap);
		min-inline-size: 0;
	}

	.note {
		margin: 0;
		padding: var(--row-pad);
		font-size: var(--text-2xs);
		color: var(--text-muted);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.empty {
		margin: 0;
		padding: var(--card-pad);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.area {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
		padding: var(--row-pad);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.area-label {
		flex: 1;
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.area-name {
		flex: 1 1 140px;
		min-inline-size: 0;
	}

	.area-fee {
		inline-size: 110px;
	}

	.n {
		font-family: var(--font-mono);
	}

	.strong {
		font-size: var(--text-sm);
		font-weight: 700;
	}

	.add {
		padding: var(--row-pad);
	}
</style>
